import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Chip,
    IconButton,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Button,
    CircularProgress,
    Divider,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    PlayArrow as RunningIcon,
    Cancel as CancelledIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { WorkflowExecution, ExecutionStatus } from '../../types';

const statusIcons = {
    [ExecutionStatus.COMPLETED]: <SuccessIcon color="success" />,
    [ExecutionStatus.FAILED]: <ErrorIcon color="error" />,
    [ExecutionStatus.PENDING]: <PendingIcon color="disabled" />,
    [ExecutionStatus.RUNNING]: <RunningIcon color="primary" />,
    [ExecutionStatus.CANCELLED]: <CancelledIcon color="warning" />,
};

const statusColors: Record<ExecutionStatus, 'success' | 'error' | 'default' | 'primary' | 'warning'> = {
    [ExecutionStatus.COMPLETED]: 'success',
    [ExecutionStatus.FAILED]: 'error',
    [ExecutionStatus.PENDING]: 'default',
    [ExecutionStatus.RUNNING]: 'primary',
    [ExecutionStatus.CANCELLED]: 'warning',
};

export default function ExecutionDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [execution, setExecution] = useState<WorkflowExecution | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchExecution();
    }, [id]);

    const fetchExecution = async () => {
        try {
            setLoading(true);
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }
            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/executions/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const rawData = await response.json();
            const data = rawData as unknown as WorkflowExecution;
            setExecution(data);
            setError(null);
        } catch (error) {
            console.error('Error fetching execution:', error);
            setError('Failed to load execution details');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchExecution();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const calculateDuration = (startDate: string, endDate?: string) => {
        const start = new Date(startDate).getTime();
        const end = endDate ? new Date(endDate).getTime() : Date.now();
        const duration = end - start;

        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        } else {
            return `${seconds}s`;
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading execution details...</Typography>
            </Box>
        );
    }

    if (error || !execution) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error || 'Execution not found'}</Typography>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/executions')}
                    sx={{ mt: 2 }}
                >
                    Back to Executions
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton
                    onClick={() => navigate('/executions')}
                    sx={{ mr: 2 }}
                >
                    <BackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Execution Details
                </Typography>
                <Chip
                    sx={{ ml: 2 }}
                    icon={statusIcons[execution.status]}
                    label={execution.status}
                    color={statusColors[execution.status]}
                />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(50% - 12px)' } }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                General Information
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText
                                        primary="Workflow ID"
                                        secondary={execution.workflowId}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Started At"
                                        secondary={formatDate(execution.startedAt)}
                                    />
                                </ListItem>
                                {execution.completedAt && (
                                    <ListItem>
                                        <ListItemText
                                            primary="Completed At"
                                            secondary={formatDate(execution.completedAt)}
                                        />
                                    </ListItem>
                                )}
                                <ListItem>
                                    <ListItemText
                                        primary="Duration"
                                        secondary={calculateDuration(execution.startedAt, execution.completedAt)}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(50% - 12px)' } }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Input Data
                            </Typography>
                            <Paper
                                sx={{
                                    p: 2,
                                    backgroundColor: 'grey.100',
                                    maxHeight: 200,
                                    overflow: 'auto',
                                }}
                            >
                                <pre style={{ margin: 0 }}>
                                    {execution.input ? JSON.stringify(execution.input, null, 2) : 'No input data'}
                                </pre>
                            </Paper>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: '1 1 100%' }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {execution.error ? 'Error' : 'Output'}
                            </Typography>
                            <Paper
                                sx={{
                                    p: 2,
                                    backgroundColor: execution.error ? 'error.light' : 'grey.100',
                                    maxHeight: 300,
                                    overflow: 'auto',
                                }}
                            >
                                <pre style={{ margin: 0 }}>
                                    {execution.error
                                        ? execution.error
                                        : execution.output
                                            ? JSON.stringify(execution.output, null, 2)
                                            : 'No output data'}
                                </pre>
                            </Paper>
                        </CardContent>
                    </Card>
                </Box>

                {execution.logs && execution.logs.length > 0 && (
                    <Box sx={{ flex: '1 1 100%' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Execution Logs
                                </Typography>
                                <Paper
                                    sx={{
                                        p: 2,
                                        backgroundColor: 'grey.100',
                                        maxHeight: 400,
                                        overflow: 'auto',
                                    }}
                                >
                                    <List dense>
                                        {execution.logs.map((log, index) => (
                                            <ListItem key={index}>
                                                <ListItemText
                                                    primary={log}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'pre-wrap',
                                                        },
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
